import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Plus, Download, Copy, Video, Phone, MapPin as MapPinIcon, Sparkles, CalendarDays, GitBranch, Mail, Send, Check, FileText } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = { VIDEO: Video, PHONE: Phone, ONSITE: MapPinIcon };

export default function InterviewsPage() {
  const [list, setList] = useState([]);
  const [cands, setCands] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availability, setAvailability] = useState(null);
  const [followUp, setFollowUp] = useState(null); // {interview, subject, body, iv_id}
  const [templates, setTemplates] = useState([]);
  const [tmplName, setTmplName] = useState("");
  const [prepPack, setPrepPack] = useState(null); // JSON preview state
  const [prepPackLoading, setPrepPackLoading] = useState(false);
  const [form, setForm] = useState({
    candidate_id: "", scheduled_at: "", duration_minutes: 45, interview_type: "VIDEO",
    interviewer_name: "", interviewer_email: "", location_or_link: "", notes: "",
  });
  const [planForm, setPlanForm] = useState({ plan_id: "", candidate_id: "", first_date: "" });

  const load = async () => {
    const [i, c, p, t] = await Promise.all([
      api.get("/interviews"), api.get("/recruit/candidates"),
      api.get("/interview-plans"), api.get("/follow-up-templates"),
    ]);
    setList(i.data); setCands(c.data); setPlans(p.data); setTemplates(t.data);
  };
  useEffect(() => { load(); }, []);

  // Reminder poller — every 60s, toast interviews starting in ≤15 minutes
  useEffect(() => {
    const notified = new Set(JSON.parse(sessionStorage.getItem("reminded") || "[]"));
    const tick = async () => {
      try {
        const { data } = await api.get("/interviews/reminders/upcoming?window_minutes=20");
        for (const iv of data.upcoming || []) {
          if (iv.minutes_away <= 15 && !notified.has(iv.interview_id)) {
            notified.add(iv.interview_id);
            sessionStorage.setItem("reminded", JSON.stringify(Array.from(notified)));
            toast(`Interview with ${iv.candidate_name} starts in ${iv.minutes_away} min`, {
              action: iv.meeting_url ? { label: "Join", onClick: () => window.open(iv.meeting_url, "_blank") } : undefined,
              duration: 15000,
            });
          }
        }
      } catch {}
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, []);

  // Load availability for the first known interviewer (or current user)
  useEffect(() => {
    if (list.length === 0) return;
    const email = list[0]?.interviewer_email;
    if (email) api.get(`/interviews/availability/${encodeURIComponent(email)}`).then(r => setAvailability(r.data)).catch(() => {});
  }, [list]);

  const upcoming = useMemo(() => list.filter(i => i.status !== "CANCELLED"), [list]);
  const highlightedDays = useMemo(() => upcoming.map(i => new Date(i.scheduled_at)), [upcoming]);
  const forSelected = useMemo(() => upcoming.filter(i => {
    const d = new Date(i.scheduled_at);
    return d.toDateString() === selectedDate.toDateString();
  }), [upcoming, selectedDate]);

  const save = async () => {
    if (!form.candidate_id || !form.scheduled_at) { toast.error("Pick a candidate and a date/time"); return; }
    setSaving(true);
    try {
      const iso = new Date(form.scheduled_at).toISOString();
      await api.post("/interviews", { ...form, scheduled_at: iso });
      setOpen(false);
      setForm({ ...form, candidate_id: "", scheduled_at: "", notes: "" });
      toast.success("Interview scheduled");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const applyPlan = async () => {
    if (!planForm.plan_id || !planForm.candidate_id || !planForm.first_date) { toast.error("Fill all fields"); return; }
    try {
      const iso = new Date(planForm.first_date).toISOString();
      const { data } = await api.post("/interview-plans/apply", { ...planForm, first_date: iso });
      toast.success(`Plan applied — ${data.interviews.length} interviews scheduled`);
      setPlanOpen(false);
      setPlanForm({ plan_id: "", candidate_id: "", first_date: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Failed"); }
  };

  const openFollowUp = async (iv) => {
    const { data } = await api.get(`/interviews/${iv.interview_id}`);
    if (!data.follow_up_email_draft) { toast.error("No draft yet — complete the meeting first"); return; }
    setFollowUp({ ...data.follow_up_email_draft, iv_id: iv.interview_id, sent: data.follow_up_email_sent });
  };

  const markSent = async () => {
    if (!followUp?.iv_id) return;
    await api.post(`/interviews/${followUp.iv_id}/mark-follow-up-sent`);
    toast.success("Marked as sent");
    setFollowUp({ ...followUp, sent: true });
    load();
  };

  const copyEmail = () => {
    if (!followUp) return;
    navigator.clipboard.writeText(`Subject: ${followUp.subject}\n\n${followUp.body}`);
    toast.success("Email copied — paste into Gmail");
  };

  const saveTemplate = async () => {
    if (!followUp || !tmplName) { toast.error("Name the template"); return; }
    try {
      await api.post("/follow-up-templates", { name: tmplName, subject: followUp.subject, body: followUp.body });
      const t = await api.get("/follow-up-templates");
      setTemplates(t.data);
      setTmplName("");
      toast.success("Template saved for the team");
    } catch (e) { toast.error("Failed"); }
  };

  const useTemplate = (id) => {
    const tmpl = templates.find(t => t.template_id === id);
    if (tmpl && followUp) setFollowUp({ ...followUp, subject: tmpl.subject, body: tmpl.body });
  };

  const bookSlot = (day, hour) => {
    const d = new Date(availability.window_start);
    d.setDate(d.getDate() + day); d.setHours(hour, 0, 0, 0);
    // datetime-local format YYYY-MM-DDTHH:mm (local tz)
    const pad = (n) => String(n).padStart(2, "0");
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setForm(f => ({ ...f, scheduled_at: local }));
    setOpen(true);
  };

  const downloadDossier = async (plan_run_id) => {
    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/interview-plans/runs/${plan_run_id}/dossier`, { credentials: "include" });
    if (!resp.ok) { toast.error("No dossier yet"); return; }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `dossier-${plan_run_id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadIcs = async (id) => {
    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/interviews/${id}/ics`, { credentials: "include" });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `interview-${id}.ics`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(".ics downloaded — open in your calendar app");
  };

  const previewPrepPack = async (iv) => {
    setPrepPackLoading(true);
    setPrepPack({ interview_id: iv.interview_id, candidate_name: iv.candidate_name, loading: true });
    try {
      const { data } = await api.get(`/interviews/${iv.interview_id}/prep-pack?format=json`);
      setPrepPack({ ...data, interview_id: iv.interview_id, candidate_name: iv.candidate_name });
    } catch (e) {
      toast.error("Couldn't load prep pack preview");
      setPrepPack(null);
    } finally {
      setPrepPackLoading(false);
    }
  };

  const downloadPrepPack = async (iv) => {
    toast.info("Preparing brief — Alfred is generating 3 tailored questions…");
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/interviews/${iv.interview_id}/prep-pack?format=pdf`, { credentials: "include" });
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prep_pack_${(iv.candidate_name || "candidate").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Prep pack downloaded");
    } catch (e) { toast.error("Failed to generate prep pack"); }
  };

  const copyMeetingUrl = (token) => {
    const url = `${window.location.origin}/meeting/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Meeting link copied");
  };

  return (
    <div className="space-y-6" data-testid="interviews-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Recruit</div>
          <h1 className="text-3xl font-bold mt-1">Interviews</h1>
          <p className="text-neutral-600 mt-1">Schedule, join and review AI-noted interviews with candidates.</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={planOpen} onOpenChange={setPlanOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="apply-plan-btn"><GitBranch className="w-4 h-4 mr-1" /> Apply plan</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Apply a multi-round plan</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label>Plan template</Label>
                  <Select value={planForm.plan_id} onValueChange={(v) => setPlanForm(f => ({...f, plan_id: v}))}>
                    <SelectTrigger className="mt-1.5" data-testid="plan-select"><SelectValue placeholder="Pick a plan…" /></SelectTrigger>
                    <SelectContent>
                      {plans.map(p => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name} · {p.stages.length} rounds</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {planForm.plan_id && (
                    <div className="mt-2 text-xs text-neutral-600 rounded-lg bg-neutral-50 p-3">
                      {plans.find(p => p.plan_id === planForm.plan_id)?.stages.map((s, i) => (
                        <div key={i} className="flex justify-between py-0.5">
                          <span>{i + 1}. {s.name}</span>
                          <span className="text-neutral-400">{s.type} · {s.duration_minutes}m · +{s.days_after_previous}d</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Candidate</Label>
                  <Select value={planForm.candidate_id} onValueChange={(v) => setPlanForm(f => ({...f, candidate_id: v}))}>
                    <SelectTrigger className="mt-1.5" data-testid="plan-candidate"><SelectValue placeholder="Pick a candidate…" /></SelectTrigger>
                    <SelectContent>{cands.map(c => <SelectItem key={c.candidate_id} value={c.candidate_id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>First round starts</Label>
                  <Input type="datetime-local" value={planForm.first_date} onChange={(e) => setPlanForm({...planForm, first_date: e.target.value})} className="mt-1.5" data-testid="plan-datetime" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPlanOpen(false)}>Cancel</Button>
                <Button className="btn-primary" onClick={applyPlan} data-testid="apply-plan-save">Schedule chain</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary rounded-full" data-testid="schedule-interview-btn"><Plus className="w-4 h-4 mr-1" /> Schedule interview</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Schedule an interview</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label>Candidate</Label>
                <Select value={form.candidate_id} onValueChange={(v) => setForm(f => ({...f, candidate_id: v}))}>
                  <SelectTrigger className="mt-1.5" data-testid="iv-candidate"><SelectValue placeholder="Pick a candidate…" /></SelectTrigger>
                  <SelectContent>
                    {cands.map(c => <SelectItem key={c.candidate_id} value={c.candidate_id}>{c.name} · {c.stage}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date & time</Label>
                  <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({...form, scheduled_at: e.target.value})} className="mt-1.5" data-testid="iv-datetime" />
                </div>
                <div>
                  <Label>Duration (mins)</Label>
                  <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({...form, duration_minutes: parseInt(e.target.value || "45")})} className="mt-1.5" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.interview_type} onValueChange={(v) => setForm(f => ({...f, interview_type: v}))}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="VIDEO">Video (auto meeting room)</SelectItem>
                      <SelectItem value="PHONE">Phone</SelectItem>
                      <SelectItem value="ONSITE">On-site</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Interviewer name</Label>
                  <Input value={form.interviewer_name} onChange={(e) => setForm({...form, interviewer_name: e.target.value})} className="mt-1.5" />
                </div>
              </div>
              {form.interview_type !== "VIDEO" && (
                <div>
                  <Label>Location or link</Label>
                  <Input value={form.location_or_link} onChange={(e) => setForm({...form, location_or_link: e.target.value})} className="mt-1.5" placeholder="Office address or phone number" />
                </div>
              )}
              <div>
                <Label>Notes</Label>
                <Textarea rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1.5" placeholder="Focus areas, questions to cover…" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={save} disabled={saving} data-testid="iv-save-btn">{saving ? "Saving…" : "Schedule"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3"><CalendarDays className="w-4 h-4 text-pink" /><h3 className="font-semibold">Calendar</h3></div>
            <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)}
              modifiers={{ hasInterview: highlightedDays }}
              modifiersClassNames={{ hasInterview: "!bg-pink !text-white !font-bold" }}
              className="rounded-lg border" data-testid="interviews-calendar" />
            <div className="mt-3 text-xs text-neutral-500">Pink dates have interviews scheduled.</div>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-3">
                {selectedDate.toDateString() === new Date().toDateString() ? "Today's interviews" : selectedDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                <span className="ml-2 text-xs text-neutral-500">({forSelected.length})</span>
              </h3>
              {forSelected.length === 0 && <div className="text-sm text-neutral-500 py-4">No interviews on this day.</div>}
              <div className="space-y-2">
                {forSelected.map(iv => {
                  const Icon = TYPE_ICON[iv.interview_type] || Video;
                  const t = new Date(iv.scheduled_at);
                  return (
                    <div key={iv.interview_id} className="rounded-lg border p-3 flex items-center gap-3" data-testid={`iv-row-${iv.interview_id}`}>
                      <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: "#FFE0F0" }}>
                        <Icon className="w-4 h-4 text-pink" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{iv.candidate_name}</div>
                        <div className="text-xs text-neutral-500">
                          {t.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})} · {iv.duration_minutes} min · {iv.interviewer_name}
                        </div>
                      </div>
                      {iv.status === "COMPLETED" && <Badge className="bg-teal text-white border-0 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Noted</Badge>}
                      {iv.status === "COMPLETED" && (
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => openFollowUp(iv)} data-testid={`iv-followup-${iv.interview_id}`}>
                          <Mail className="w-3 h-3 mr-1" /> Follow-up
                        </Button>
                      )}
                      {iv.meeting_url && iv.status !== "COMPLETED" && (
                        <>
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => copyMeetingUrl(iv.meeting_token)} data-testid={`iv-copy-${iv.interview_id}`}><Copy className="w-3 h-3 mr-1" /> Copy link</Button>
                          <a href={`/meeting/${iv.meeting_token}`} target="_blank" rel="noreferrer">
                            <Button size="sm" className="btn-primary text-xs" data-testid={`iv-join-${iv.interview_id}`}>Join</Button>
                          </a>
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => downloadIcs(iv.interview_id)} data-testid={`iv-ics-${iv.interview_id}`}><Download className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => previewPrepPack(iv)} data-testid={`iv-prep-${iv.interview_id}`}>
                        <FileText className="w-3.5 h-3.5 mr-1" /> Prep pack
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm" data-testid="availability-card">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-3">Interviewer availability · next 14 days</h3>
              {availability ? (
                <AvailabilityHeatmap availability={availability} onSlotClick={bookSlot} />
              ) : (
                <div className="text-sm text-neutral-500 py-3">No availability data yet.</div>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-3">All upcoming ({upcoming.length})</h3>
              <div className="space-y-2">
                {upcoming.map(iv => {
                  const t = new Date(iv.scheduled_at);
                  return (
                    <div key={iv.interview_id} className="flex items-center gap-3 rounded-lg border p-3 hover:border-pink transition-colors" data-testid={`iv-upcoming-${iv.interview_id}`}>
                      <Link to={`/app/recruit/candidates/${iv.candidate_id}`} className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="text-center min-w-[52px]">
                          <div className="text-[10px] uppercase text-neutral-500">{t.toLocaleDateString(undefined, { month: "short" })}</div>
                          <div className="text-lg font-bold">{t.getDate()}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{iv.candidate_name}</div>
                          <div className="text-xs text-neutral-500">{t.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})} · {iv.interview_type} · {iv.interviewer_name}
                            {iv.plan_stage_name && <span className="ml-2 text-pink">· {iv.plan_stage_name}</span>}
                          </div>
                        </div>
                      </Link>
                      <Badge variant="outline" className="text-[10px]">{iv.status}</Badge>
                      {iv.plan_run_id && (
                        <Button size="sm" variant="ghost" title="Download round-by-round dossier PDF"
                          onClick={() => downloadDossier(iv.plan_run_id)} data-testid={`dossier-${iv.plan_run_id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  );
                })}
                {upcoming.length === 0 && <div className="text-sm text-neutral-500 py-4">No interviews scheduled.</div>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Interviewer Prep Pack preview dialog */}
      <Dialog open={!!prepPack} onOpenChange={(v) => !v && setPrepPack(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="prep-pack-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-pink" /> Interviewer prep pack
            </DialogTitle>
          </DialogHeader>
          {prepPackLoading || prepPack?.loading ? (
            <div className="py-10 text-center text-sm text-neutral-500">
              <Sparkles className="w-4 h-4 text-pink inline mr-1 animate-pulse" />
              Alfred is generating 3 tailored questions from behavioural gaps…
            </div>
          ) : prepPack ? (
            <div className="space-y-4">
              {/* Header block */}
              <div className="rounded-xl bg-navy text-white p-4" style={{ backgroundColor: "#00214D" }} data-testid="prep-header">
                <div className="text-[10px] uppercase tracking-widest text-pink font-semibold">Prep pack</div>
                <div className="text-lg font-bold mt-0.5">
                  {prepPack.candidate?.name} <span className="text-white/60 font-normal">→ {prepPack.job?.title || "—"}</span>
                </div>
                <div className="text-xs text-white/60 mt-1">
                  {prepPack.interview?.scheduled_at ? new Date(prepPack.interview.scheduled_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—"}
                  {prepPack.interview?.round ? <> · Round {prepPack.interview.round}</> : null}
                  {prepPack.interview?.interviewers?.length ? <> · With {prepPack.interview.interviewers.join(", ")}</> : null}
                </div>
              </div>

              {/* Role snapshot */}
              <section data-testid="prep-role">
                <div className="text-xs font-semibold text-navy uppercase tracking-widest mb-1.5" style={{ color: "#00214D" }}>Role at a glance</div>
                <div className="text-sm text-neutral-700">
                  <span className="font-semibold">{prepPack.job?.title}</span>
                  {prepPack.job?.department ? <> · {prepPack.job.department}</> : null}
                  {prepPack.job?.location ? <> · {prepPack.job.location}</> : null}
                </div>
                {prepPack.job?.requirements?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="prep-requirements">
                    {prepPack.job.requirements.map((r, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-teal/10 text-teal border border-teal/20">{r}</span>
                    ))}
                  </div>
                )}
                {prepPack.job?.description_excerpt && (
                  <div className="text-xs text-neutral-600 mt-2 whitespace-pre-wrap max-h-28 overflow-y-auto leading-relaxed border-l-2 border-neutral-200 pl-3">
                    {prepPack.job.description_excerpt}
                  </div>
                )}
              </section>

              {/* Behavioural signals */}
              {prepPack.behavioral?.scores && (
                <section data-testid="prep-behavioral">
                  <div className="text-xs font-semibold text-navy uppercase tracking-widest mb-1.5" style={{ color: "#00214D" }}>Behavioural signals</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {Object.entries(prepPack.behavioral.scores).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 text-xs">
                        <div className="w-28 text-neutral-600 capitalize shrink-0">{k.replaceAll("_", " ")}</div>
                        <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                          <div className="h-full bg-pink" style={{ width: `${v}%` }} />
                        </div>
                        <div className="w-8 text-right font-mono font-semibold">{v}</div>
                      </div>
                    ))}
                  </div>
                  {prepPack.behavioral.strength && (
                    <div className="text-[11px] text-neutral-500 mt-2">Strength profile: <span className="font-semibold text-neutral-700">{prepPack.behavioral.strength}</span> · Overall {prepPack.behavioral.overall_score}/100</div>
                  )}
                  {prepPack.behavioral.summary && (
                    <div className="text-xs text-neutral-700 mt-2 italic">{prepPack.behavioral.summary}</div>
                  )}
                  {(prepPack.behavioral.strengths?.length || prepPack.behavioral.watch_outs?.length) ? (
                    <div className="grid sm:grid-cols-2 gap-2 mt-2">
                      {prepPack.behavioral.strengths?.length > 0 && (
                        <div className="rounded-lg border border-teal/20 bg-teal/5 p-2">
                          <div className="text-[10px] uppercase font-semibold text-teal">Strengths</div>
                          <div className="text-xs text-neutral-700 mt-1">{prepPack.behavioral.strengths.join(" · ")}</div>
                        </div>
                      )}
                      {prepPack.behavioral.watch_outs?.length > 0 && (
                        <div className="rounded-lg border border-pink/20 bg-pink/5 p-2">
                          <div className="text-[10px] uppercase font-semibold text-pink">Watch-outs</div>
                          <div className="text-xs text-neutral-700 mt-1">{prepPack.behavioral.watch_outs.join(" · ")}</div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </section>
              )}

              {/* Resume */}
              {prepPack.candidate?.resume_summary && (
                <section data-testid="prep-resume">
                  <div className="text-xs font-semibold text-navy uppercase tracking-widest mb-1.5" style={{ color: "#00214D" }}>Resume highlights</div>
                  <div className="text-xs text-neutral-700 whitespace-pre-wrap max-h-32 overflow-y-auto leading-relaxed">
                    {prepPack.candidate.resume_summary}
                  </div>
                </section>
              )}

              {/* Tailored questions */}
              <section data-testid="prep-questions">
                <div className="text-xs font-semibold text-navy uppercase tracking-widest mb-1.5 flex items-center gap-1" style={{ color: "#00214D" }}>
                  <Sparkles className="w-3 h-3 text-pink" /> 3 questions to ask today
                </div>
                <ol className="space-y-2">
                  {(prepPack.tailored_questions || []).map((q, i) => (
                    <li key={i} className="flex gap-3 rounded-lg border border-pink/20 bg-pink/5 p-3" data-testid={`prep-question-${i}`}>
                      <div className="text-pink font-bold font-mono text-sm shrink-0">{String(i + 1).padStart(2, "0")}</div>
                      <div className="text-sm text-neutral-800">{q}</div>
                    </li>
                  ))}
                </ol>
              </section>

              <div className="text-[10px] text-neutral-400 pt-2">
                Alfred generated the 3 questions from behavioural gaps vs role needs — feel free to add your own.
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPrepPack(null)} data-testid="prep-close-btn">Close</Button>
            <Button
              className="btn-primary"
              disabled={!prepPack || prepPack.loading}
              onClick={() => {
                if (prepPack) {
                  downloadPrepPack({ interview_id: prepPack.interview_id, candidate_name: prepPack.candidate_name });
                }
              }}
              data-testid="prep-download-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Follow-up email dialog */}
      <Dialog open={!!followUp} onOpenChange={(v) => !v && setFollowUp(null)}>
        <DialogContent className="max-w-lg" data-testid="followup-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4 text-pink" /> Auto-drafted follow-up</DialogTitle></DialogHeader>
          {followUp && (
            <div className="space-y-3">
              {templates.length > 0 && (
                <div className="rounded-lg bg-neutral-50 p-3 flex items-center gap-2">
                  <Label className="text-xs shrink-0">Use template</Label>
                  <Select onValueChange={useTemplate}>
                    <SelectTrigger className="flex-1" data-testid="template-select"><SelectValue placeholder="Pick a saved template…" /></SelectTrigger>
                    <SelectContent>{templates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Subject</Label>
                <Input value={followUp.subject || ""} onChange={(e) => setFollowUp({ ...followUp, subject: e.target.value })} className="mt-1.5" data-testid="followup-subject" />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea rows={10} value={followUp.body || ""} onChange={(e) => setFollowUp({ ...followUp, body: e.target.value })} className="mt-1.5" data-testid="followup-body" />
              </div>
              <div className="flex items-center gap-2 pt-2 border-t">
                <Input placeholder="Save as template…" value={tmplName} onChange={(e) => setTmplName(e.target.value)} className="flex-1" data-testid="save-template-name" />
                <Button variant="outline" onClick={saveTemplate} disabled={!tmplName} className="rounded-full" data-testid="save-template-btn">Save for team</Button>
              </div>
              <div className="text-[11px] text-neutral-500">Alfred drafted this from the AI meeting notes. Review and edit before sending.</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFollowUp(null)}>Close</Button>
            <Button variant="outline" onClick={copyEmail} data-testid="followup-copy-btn"><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
            <Button className="btn-primary" onClick={markSent} disabled={followUp?.sent} data-testid="followup-sent-btn">
              {followUp?.sent ? <><Check className="w-3.5 h-3.5 mr-1" /> Sent</> : <><Send className="w-3.5 h-3.5 mr-1" /> Mark sent</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AvailabilityHeatmap({ availability, onSlotClick }) {
  const days = 14, hours = [9, 10, 11, 12, 13, 14, 15, 16, 17];
  const start = new Date(availability.window_start);
  const busySet = new Set();
  for (const b of availability.busy) {
    const s = new Date(b.start), e = new Date(b.end);
    for (let d = new Date(s); d < e; d = new Date(d.getTime() + 30 * 60000)) {
      busySet.add(`${d.toDateString()}|${d.getHours()}`);
    }
  }
  const cell = (day, hour) => {
    const d = new Date(start); d.setDate(d.getDate() + day); d.setHours(hour, 0, 0, 0);
    const isBusy = busySet.has(`${d.toDateString()}|${hour}`);
    return (
      <button key={`${day}-${hour}`} title={`${d.toLocaleString()} · ${isBusy ? "Busy" : "Free — click to book"}`}
              onClick={() => !isBusy && onSlotClick?.(day, hour)}
              disabled={isBusy}
              className={`w-4 h-4 rounded-sm transition-transform ${isBusy ? "bg-pink cursor-not-allowed" : "bg-teal/40 hover:bg-teal hover:scale-125 cursor-pointer"}`}
              data-testid={`heat-${day}-${hour}`} />
    );
  };
  return (
    <div className="overflow-x-auto">
      <div className="inline-block">
        <div className="flex gap-2 items-center mb-2">
          <span className="text-[10px] text-neutral-500 w-10"></span>
          {Array.from({ length: days }).map((_, i) => {
            const d = new Date(start); d.setDate(d.getDate() + i);
            return <div key={i} className="w-4 text-[9px] text-center text-neutral-500">{d.getDate()}</div>;
          })}
        </div>
        {hours.map(h => (
          <div key={h} className="flex gap-2 items-center mb-1">
            <span className="text-[10px] text-neutral-500 w-10 font-mono">{String(h).padStart(2, "0")}:00</span>
            {Array.from({ length: days }).map((_, i) => cell(i, h))}
          </div>
        ))}
        <div className="mt-3 flex items-center gap-3 text-[10px] text-neutral-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-teal/40" /> Free</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-pink" /> Busy</span>
        </div>
      </div>
    </div>
  );
}
